/**
 * Normalizes a department name or code into a standardized canonical key
 * @param {string} dept 
 * @returns {string}
 */
export const normalizeDepartment = (dept) => {
  if (!dept) return '';
  const d = String(dept).toLowerCase().trim().replace(/[-_]/g, ' ');
  
  if (['media', 'media hub', 'social media', 'social media department', 'social', 'mediahub', 'media dept', 'media department'].includes(d) || d.includes('media')) {
    return 'media';
  }
  if (['graphic', 'graphics', 'graphic design', 'graphic department', 'graphics department', 'design', 'creative'].includes(d) || d.includes('graphic')) {
    return 'graphic';
  }
  if (['video', 'video department', 'video editing', 'video production', 'editing'].includes(d) || d.includes('video')) {
    return 'video';
  }
  if (['it', 'information technology', 'it department', 'tech', 'technology', 'software'].includes(d) || d === 'it') {
    return 'it';
  }
  if (['strategy', 'creative strategy', 'strategy department'].includes(d) || d.includes('strategy')) {
    return 'strategy';
  }
  if (['production', 'production department'].includes(d) || d.includes('production')) {
    return 'production';
  }
  if (['operations', 'ops', 'operation', 'operations department'].includes(d) || d.includes('operation')) {
    return 'operations';
  }
  if (['finance', 'accounts', 'finance department', 'accounting'].includes(d) || d.includes('finance') || d.includes('account')) {
    return 'finance';
  }
  if (['hr', 'human resources', 'human resource', 'hr department'].includes(d) || d === 'hr') {
    return 'hr';
  }
  if (['business development', 'bd', 'sales', 'bd department', 'sales department'].includes(d) || d.includes('business development') || d === 'bd') {
    return 'business_development';
  }
  return d;
};

/**
 * Helper to determine if a department is visible to a logged-in user
 * based on their role and assigned department.
 * 
 * Rules:
 * - Admin, HR, Manager, Director see all departments.
 * - Production Head sees Production plus the 5 creative/tech departments:
 *   Production, Graphic/Graphics, Video, IT/Information Technology, Media/Media Hub/Social Media, Strategy/Creative Strategy.
 * - Department Head sees only their own department (e.g. Media Hub dept head only sees Media Hub).
 * - Regular Employees see only their own department.
 * 
 * @param {string} deptCode 
 * @param {string} deptName 
 * @param {object} user 
 * @returns {boolean}
 */
export const isDeptVisibleToUser = (deptCode, deptName, user) => {
  if (!user) return false;
  const role = user.role;
  const userDept = (user.department || '').toLowerCase().trim();

  // Admin, HR, Manager, Director see everything
  if (role === 'admin' || role === 'hr' || role === 'manager' || role === 'director') {
    return true;
  }

  const code = (deptCode || '').toLowerCase().trim();
  const name = (deptName || '').toLowerCase().trim();
  const targetNormCode = normalizeDepartment(code);
  const targetNormName = normalizeDepartment(name);

  // Production Head can see Production plus group A (creative/tech) departments
  if (role === 'production_head') {
    const allowedDepts = ['production', 'graphic', 'video', 'it', 'media', 'strategy'];
    return allowedDepts.includes(targetNormCode) || allowedDepts.includes(targetNormName);
  }

  // Department Head, Employee & others can ONLY see their own department
  if (!userDept) return false;
  const userNorm = normalizeDepartment(userDept);

  // 1. Direct normalized match (e.g. 'media' === 'media' for 'Media Hub' and 'Media')
  if (userNorm && (targetNormCode === userNorm || targetNormName === userNorm)) {
    return true;
  }

  // 2. Direct string equality match
  if (code === userDept || name === userDept) {
    return true;
  }

  // 3. Substring matching (e.g. userDept 'Media Hub' contains code 'Media')
  if (code && (userDept.includes(code) || code.includes(userDept))) {
    return true;
  }
  if (name && (userDept.includes(name) || name.includes(userDept))) {
    return true;
  }

  return false;
};
